import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ChevronLeft, ChevronRight, Database, RefreshCw, Search, ShieldCheck, X } from "lucide-react";
import { supabase } from "./supabase";
import type { DataDataset, DataExplorerPage, PortfolioDirectoryData } from "./types";

const pageSize = 50;

const datasets: Array<{ id: DataDataset; label: string; description: string }> = [
  { id: "clients", label: "Clients", description: "Roster, accounts, contacts, and matched websites" },
  { id: "leads", label: "Leads", description: "Contact, attribution, qualification, and revenue" },
  { id: "properties", label: "Properties", description: "Sites, markets, hosting, and operating status" },
  { id: "interactions", label: "Interactions", description: "Cross-channel lead activity" },
  { id: "sms_messages", label: "SMS", description: "Inbound and outbound message ledger" },
  { id: "voice_calls", label: "Voice", description: "Call routing, outcomes, and duration" },
  { id: "webhook_events", label: "Webhooks", description: "Provider intake and processing state" },
  { id: "sync_runs", label: "Sync runs", description: "Connector volume, failures, and mapping" },
  { id: "callrail_trackers", label: "CallRail", description: "Verified company and tracker inventory" },
];

function humanize(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function displayValue(column: string, value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
  if (typeof value === "object") return JSON.stringify(value);
  const text = String(value);
  if ((column.endsWith("_at") || column === "received_at" || column === "started_at" || column === "finished_at") && !Number.isNaN(Date.parse(text))) {
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(text));
  }
  return text;
}

function csvCell(value: unknown): string {
  const text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

function downloadCsv(page: DataExplorerPage) {
  const lines = [page.columns.map(csvCell).join(","), ...page.rows.map((row) => page.columns.map((column) => csvCell(row[column])).join(","))];
  const blob = new Blob(["\ufeff", lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${page.dataset}-${new Date().toISOString().slice(0, 10)}-rows-${page.offset + 1}-${page.offset + page.rows.length}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function DataExplorer({ initialPage = null, preview = false, directory = null }: { initialPage?: DataExplorerPage | null; preview?: boolean; directory?: PortfolioDirectoryData | null }) {
  const [dataset, setDataset] = useState<DataDataset>("leads");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<DataExplorerPage | null>(initialPage);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [propertyId, setPropertyId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [recordType, setRecordType] = useState("all");

  const selected = useMemo(() => datasets.find((item) => item.id === dataset) ?? datasets[0], [dataset]);

  const load = useCallback(async () => {
    if (preview) { setLoading(false); return; }
    setLoading(true);
    setError("");
    const { data, error: loadError } = await supabase.rpc("dashboard_data_page_v2", { p_request: {
      dataset, limit: pageSize, offset, search: appliedQuery || null, status: status || null,
      property_id: propertyId, client_id: clientId, record_type: recordType,
    } });
    if (loadError) {
      setError(`${loadError.message}. Try refreshing, or confirm the data-explorer migration has been applied.`);
    } else if (data) {
      setPage(data as DataExplorerPage);
    }
    setLoading(false);
  }, [appliedQuery, clientId, dataset, offset, preview, propertyId, recordType, status]);

  useEffect(() => { void load(); }, [load]);

  const chooseDataset = (next: DataDataset) => {
    setDataset(next);
    setOffset(0);
    setQuery("");
    setAppliedQuery("");
    setStatus("");
    setPropertyId(null);
    setClientId(null);
    setRecordType("all");
    if (!preview) setPage(null);
  };

  const search = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setAppliedQuery(query.trim());
  };

  const total = Number(page?.total_count || 0);
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rowStart = total ? offset + 1 : 0;
  const rowEnd = Math.min(offset + (page?.rows.length || 0), total);
  const structuredFilters = dataset === "clients" || dataset === "properties" || dataset === "leads";
  const statusOptions = dataset === "clients" ? ["active","inactive"] : dataset === "properties" ? ["active","onboarding","paused"] : ["new","validating","qualified","rejected","spam"];
  const hasFilters = Boolean(appliedQuery || status || propertyId || clientId || recordType !== "all");

  return <div className="data-explorer">
    <section className="explorer-intro" aria-labelledby="explorer-title">
      <div>
        <h2 id="explorer-title">One ledger for the operating system.</h2>
        <p>Search the core Supabase datasets without opening the database console. Every result comes through a fixed, audited dataset allowlist.</p>
      </div>
      <div className="explorer-access"><ShieldCheck /><span><strong>{page?.role || "Protected"} access</strong><small>Sensitive values follow your dashboard role</small></span></div>
    </section>

    <nav className="dataset-rail" aria-label="Available datasets">
      {datasets.map((item) => <button key={item.id} className={dataset === item.id ? "active" : ""} onClick={() => chooseDataset(item.id)} aria-current={dataset === item.id ? "page" : undefined}>
        <span>{item.label}</span><small>{item.description}</small>
      </button>)}
    </nav>

    <section className="section table-section explorer-ledger" aria-labelledby="dataset-title">
      <header className="explorer-toolbar">
        <div><Database /><span><h3 id="dataset-title">{selected.label}</h3><small>{total.toLocaleString()} records · {selected.description}</small></span></div>
        <form className="explorer-search" onSubmit={search} role="search">
          <label className="search"><Search /><span className="sr-only">Search {selected.label}</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${selected.label.toLowerCase()}`} /></label>
          <button className="button button--quiet" type="submit">Search</button>
          <button className="icon-button explorer-refresh" type="button" onClick={() => void load()} aria-label={`Refresh ${selected.label}`} disabled={loading}><RefreshCw className={loading ? "spin" : ""} /></button>
          <button className="button button--quiet" type="button" onClick={() => page && downloadCsv(page)} disabled={!page?.rows.length}><ArrowDownRight /> Export page</button>
        </form>
      </header>

      {structuredFilters && <div className="explorer-filter-bar" aria-label={`${selected.label} filters`}>
        <label className="compact-filter"><span>Status</span><select value={status} onChange={(event) => { setOffset(0); setStatus(event.target.value); }}><option value="">All statuses</option>{statusOptions.map((value) => <option key={value} value={value}>{humanize(value)}</option>)}</select></label>
        <label className="compact-filter"><span>Client</span><select value={clientId || ""} onChange={(event) => { setOffset(0); setClientId(event.target.value ? Number(event.target.value) : null); }}><option value="">All clients</option>{directory?.clients.map((client) => <option key={client.client_id} value={client.client_id}>{client.display_name}</option>)}</select></label>
        {(dataset === "properties" || dataset === "leads") && <label className="compact-filter"><span>Website</span><select value={propertyId || ""} onChange={(event) => { setOffset(0); setPropertyId(event.target.value ? Number(event.target.value) : null); }}><option value="">All websites</option>{directory?.properties.map((property) => <option key={property.property_id} value={property.property_id}>{property.property_name}</option>)}</select></label>}
        {dataset === "leads" && <label className="compact-filter"><span>Records</span><select value={recordType} onChange={(event) => { setOffset(0); setRecordType(event.target.value); }}><option value="all">All records</option><option value="production">Production only</option><option value="qa">QA only</option><option value="duplicates">Duplicates only</option></select></label>}
        {hasFilters && <button className="text-button filter-clear" type="button" onClick={() => { setQuery(""); setAppliedQuery(""); setStatus(""); setPropertyId(null); setClientId(null); setRecordType("all"); setOffset(0); }}><X /> Reset filters</button>}
      </div>}

      {error && <div className="explorer-error" role="alert"><strong>Couldn’t load {selected.label}.</strong><span>{error}</span><button className="text-button" onClick={() => void load()}>Try again</button></div>}
      {!error && <div className={`table-wrap explorer-table-wrap ${loading ? "is-loading" : ""}`} aria-busy={loading}>
        <table className="explorer-table"><thead><tr>{page?.columns.map((column) => <th key={column}>{humanize(column)}</th>)}</tr></thead>
          <tbody>{page?.rows.map((row, rowIndex) => <tr key={`${dataset}-${offset + rowIndex}`}>{page.columns.map((column) => {
            const value = displayValue(column, row[column]);
            const long = ["summary", "message", "error", "campaign", "tracking_numbers"].includes(column);
            return <td key={column} className={long ? "data-cell--long" : undefined} title={value === "—" ? undefined : value}>{value}</td>;
          })}</tr>)}</tbody></table>
        {loading && <div className="explorer-loading"><RefreshCw className="spin" /><span>Reading {selected.label.toLowerCase()}…</span></div>}
      </div>}

      {!loading && !error && !page?.rows.length && <div className="explorer-empty"><Database /><strong>No matching records</strong><span>{appliedQuery ? `Nothing matched “${appliedQuery}”. Clear the search and try again.` : `No ${selected.label.toLowerCase()} have landed in Supabase yet.`}</span></div>}

      <footer className="explorer-pagination">
        <span>Rows {rowStart.toLocaleString()}–{rowEnd.toLocaleString()} of {total.toLocaleString()}{appliedQuery ? ` matching “${appliedQuery}”` : ""}</span>
        <div><button className="button button--quiet" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - pageSize))}><ChevronLeft /> Previous</button><span>Page {currentPage} of {totalPages}</span><button className="button button--quiet" disabled={offset + pageSize >= total || loading} onClick={() => setOffset(offset + pageSize)}>Next <ChevronRight /></button></div>
      </footer>
    </section>
  </div>;
}
